/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { accessAPI, downloadFile, formatFecha, TablaFiltrable } from "../../shared";
import { Titulo, EstadoCertificado, CampoInformacion } from "../../shared";
import { Boton, ActividadesTimeline, ArchivoPreview } from "../../shared";

import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Menu } from "primereact/menu";
import { Image } from "primereact/image";
import { Skeleton } from "primereact/skeleton";

export default function ProcesoAuditado() {
  const [loader, setLoader] = useState(true);
  const [procesoAuditado, setProcesoAuditado] = useState();
  const [expandedRows, setExpandedRows] = useState(null);
  const [loaderDescarga, setLoaderDescarga] = useState(false);
  const [archivoDescargando, setArchivoDescargando] = useState(null);
  const [etiquetaDescargando, setEtiquetaDescargando] = useState(null);
  const [imagenesVerificaciones, setImagenesVerificaciones] = useState({});

  const toast = useRef(null);
  const menuRefs = useRef({});
  const { auditoriaId, procesoAuditadoId } = useParams();

  useEffect(() => {
    accessAPI(
      "GET",
      `auditorsg/auditoria/${auditoriaId}/proceso/${procesoAuditadoId}`,
      null,
      (respuesta) => {
        setProcesoAuditado(respuesta);
        setLoader(false);
      },
      (respuesta) => {
        setLoader(false);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar el proceso",
          sticky: true,
        });
      },
    );
  }, [auditoriaId, procesoAuditadoId]);

  // Función para cargar imagen de verificación
  const cargarImagenVerificacion = useCallback(
    (verificacionId) => {
      if (imagenesVerificaciones[verificacionId]) {
        return; // Ya está cargada
      }

      accessAPI(
        "GET",
        `auditorsg/auditoria/comentariocampo/${verificacionId}/imagen`,
        null,
        (blob) => {
          const url = URL.createObjectURL(blob);
          setImagenesVerificaciones((prev) => ({
            ...prev,
            [verificacionId]: url,
          }));
        },
        (error) => {
          console.error("Error cargando imagen:", error);
        },
      );
    },
    [imagenesVerificaciones],
  );

  // Función para descargar etiqueta
  const descargarEtiqueta = (producto) => {
    if (producto.etiqueta && producto.etiqueta.length > 0) {
      const etiqueta = producto.etiqueta[0];
      const nombreArchivo =
        etiqueta.archivo?.nombredescarga || "etiqueta.pdf";

      setEtiquetaDescargando(producto.id);
      downloadFile(
        `auditorsg/auditoria/descargaretiqueta/${etiqueta.id}`,
        nombreArchivo,
        () => {
          setEtiquetaDescargando(null);
        },
        (error) => {
          setEtiquetaDescargando(null);
          if (toast.current) {
            toast.current.show({
              severity: "error",
              summary: "Error",
              detail: error[0]?.msg || "No se pudo descargar la etiqueta",
              sticky: true,
            });
          }
        },
      );
    }
  };

  // Función para descargar el certificado
  const descargarCertificado = () => {
    const certificado = procesoAuditado?.proceso?.certificado?.[0];
    if (!certificado) return;

    const nombreArchivo =
      certificado.emisioncertificado?.[0]?.archivopdf?.nombredescarga ||
      "certificado.pdf";

    setLoaderDescarga(true);
    downloadFile(
      `auditorsg/auditoria/descargarcertificado/${certificado.id}`,
      nombreArchivo,
      () => {
        setLoaderDescarga(false);
      },
      (error) => {
        setLoaderDescarga(false);
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: error[0]?.msg || "No se pudo descargar el certificado",
            sticky: true,
          });
        }
      },
    );
  };

  // Verificar si el certificado tiene PDF disponible
  const tieneCertificadoPDF = () => {
    const certificado = procesoAuditado?.proceso?.certificado?.[0];
    return certificado?.emisioncertificado?.[0]?.archivopdf?.nombredescarga;
  };

  // Obtener todos los archivos del proceso y de sus actividades
  const getArchivos = () => {
    const archivos = [];
    if (
      procesoAuditado?.proceso?.archivos &&
      Array.isArray(procesoAuditado.proceso.archivos)
    ) {
      procesoAuditado.proceso.archivos.forEach((archivo) => {
        archivos.push({
          ...archivo,
          origen: "Proceso",
        });
      });
    }
    if (procesoAuditado?.proceso?.actividad) {
      procesoAuditado.proceso.actividad.forEach((actividad) => {
        if (actividad.archivos && Array.isArray(actividad.archivos)) {
          actividad.archivos.forEach((archivo) => {
            archivos.push({
              ...archivo,
              origen: actividad.actividadtipo?.nombre,
              actividadFecha: actividad.fechacreacion,
            });
          });
        }
      });
    }
    return archivos;
  };

  // Función para descargar archivo (proceso o actividad) por hash
  const descargarArchivoActividad = (archivo) => {
    setArchivoDescargando(archivo.id);
    downloadFile(
      `archivo/${archivo.hash}/download`,
      archivo.nombre,
      () => {
        setArchivoDescargando(null);
      },
      (error) => {
        setArchivoDescargando(null);
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: error[0]?.msg || "No se pudo descargar el archivo",
            sticky: true,
          });
        }
      },
    );
  };

  // Template para la columna de acciones
  const actionBodyTemplate = (data, options) => {
    const producto = procesoAuditado.proceso.producto[options.rowIndex];

    // Solo mostrar el menú si el producto tiene etiqueta
    if (!producto || !producto.etiqueta || producto.etiqueta.length === 0) {
      return null;
    }

    const items = [
      {
        label: "Descargar etiqueta",
        icon: "pi pi-download",
        command: () => descargarEtiqueta(producto),
      },
    ];

    return (
      <div>
        <Menu
          model={items}
          popup
          ref={(el) => (menuRefs.current[producto.id] = el)}
          id={`popup_menu_${producto.id}`}
        />
        <Boton
          callback={(event) => menuRefs.current[producto.id]?.toggle(event)}
          icono
          chico
          negro
        >
          <i className="pi pi-ellipsis-v"></i>
        </Boton>
      </div>
    );
  };

  // Template para el contenido expandido de cada fila
  const rowExpansionTemplate = (data, options) => {
    const productoCompleto = procesoAuditado.proceso.producto[options.index];

    if (!productoCompleto) {
      return <p>No hay información adicional</p>;
    }

    return (
      <div
        css={{
          padding: "1em",
          backgroundColor: "#f8f9fa",
          borderRadius: "6px",
          position: "relative",
        }}
      >
        {productoCompleto.etiqueta?.length > 0 && (
          <div
            css={{
              top: "1em",
              right: "1em",
              marginBottom: "1em",
              textAlign: "right",
              width: "100%",
            }}
          >
            <Boton
              callback={() => descargarEtiqueta(productoCompleto)}
              loader={etiquetaDescargando === productoCompleto.id}
            >
              <i className="pi pi-download"></i> Descargar etiqueta
            </Boton>
          </div>
        )}
        {productoCompleto.producto_x_productocampospropios?.map((campo) => (
          <div
            key={campo.id}
            css={{
              marginBottom: "1.5em",
              padding: "1em",
              backgroundColor: "white",
              borderRadius: "6px",
              border: "1px solid #e0e0e0",
            }}
          >
            <div css={{ marginBottom: "0.5em" }}>
              <strong css={{ color: "var(--grisTextos)" }}>
                {campo.productocampospropios.etiqueta}
              </strong>
            </div>

            <div css={{ fontSize: "1.1em", marginBottom: "0.75em" }}>
              {campo.valor || "N/A"}
            </div>

            {/* Histórico de valores */}
            {campo.historico && campo.historico.length > 0 && (
              <div css={{ marginTop: "0.75em" }}>
                <small
                  css={{
                    color: "var(--grisTextos)",
                    fontWeight: "600",
                    display: "block",
                    marginBottom: "0.5em",
                  }}
                >
                  <i
                    className="pi pi-history"
                    css={{ marginRight: "0.5em" }}
                  ></i>
                  Histórico de valores
                </small>
                <div
                  css={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5em",
                  }}
                >
                  {campo.historico.map((hist, idx) => (
                    <div
                      key={idx}
                      css={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "0.75em",
                        padding: "0.5em",
                        backgroundColor:
                          hist.aprobacion === true
                            ? "rgba(76, 175, 80, 0.1)"
                            : hist.aprobacion === false
                              ? "rgba(244, 67, 54, 0.1)"
                              : "rgba(158, 158, 158, 0.1)",
                        borderRadius: "4px",
                        borderLeft: `3px solid ${
                          hist.aprobacion === true
                            ? "var(--verde)"
                            : hist.aprobacion === false
                              ? "var(--rojo)"
                              : "#9e9e9e"
                        }`,
                      }}
                    >
                      <i
                        className={
                          hist.aprobacion === true
                            ? "pi pi-check"
                            : hist.aprobacion === false
                              ? "pi pi-times"
                              : "pi pi-pencil"
                        }
                        css={{
                          color:
                            hist.aprobacion === true
                              ? "var(--verde)"
                              : hist.aprobacion === false
                                ? "var(--rojo)"
                                : "#9e9e9e",
                          marginTop: "0.2em",
                        }}
                      ></i>
                      <div css={{ flex: 1 }}>
                        <div
                          css={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <small css={{ fontWeight: "600" }}>
                            {hist.colaborador || "Sistema"}
                          </small>
                          <small css={{ color: "#999" }}>
                            {hist.fecha && formatFecha(hist.fecha)}
                          </small>
                        </div>
                        {hist.valor && (
                          <div
                            css={{ fontSize: "0.95em", marginTop: "0.25em" }}
                          >
                            {hist.valor}
                          </div>
                        )}
                        {hist.comentario && (
                          <small
                            css={{
                              display: "block",
                              marginTop: "0.25em",
                              fontStyle: "italic",
                            }}
                          >
                            "{hist.comentario}"
                          </small>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Verificaciones */}
            {campo.ComentarioCampo?.length > 0 && (
              <div css={{ marginTop: "0.75em" }}>
                <small
                  css={{
                    color: "var(--grisTextos)",
                    fontWeight: "600",
                    display: "block",
                    marginBottom: "0.5em",
                  }}
                >
                  <i
                    className="pi pi-verified"
                    css={{ marginRight: "0.5em" }}
                  ></i>
                  Verificaciones
                </small>
                <div
                  css={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5em",
                  }}
                >
                  {campo.ComentarioCampo.map((verificacion) => {
                    // Cargar imagen si tiene archivo
                    if (
                      verificacion.archivoId &&
                      !imagenesVerificaciones[verificacion.id]
                    ) {
                      cargarImagenVerificacion(verificacion.id);
                    }

                    return (
                      <div
                        key={verificacion.id}
                        css={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "0.75em",
                          padding: "0.5em",
                          backgroundColor: verificacion.aprobado
                            ? "rgba(76, 175, 80, 0.1)"
                            : "rgba(244, 67, 54, 0.1)",
                          borderRadius: "4px",
                          borderLeft: `3px solid ${
                            verificacion.aprobado
                              ? "var(--verde)"
                              : "var(--rojo)"
                          }`,
                        }}
                      >
                        <i
                          className={
                            verificacion.aprobado
                              ? "pi pi-check"
                              : "pi pi-times"
                          }
                          css={{
                            color: verificacion.aprobado
                              ? "var(--verde)"
                              : "var(--rojo)",
                            marginTop: "0.2em",
                          }}
                        ></i>
                        <div css={{ flex: 1 }}>
                          <div
                            css={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <small css={{ fontWeight: "600" }}>
                              {verificacion.auditor?.nombre}{" "}
                              {verificacion.auditor?.apellido}
                            </small>
                            <small css={{ color: "#999" }}>
                              {formatFecha(verificacion.fecha)}
                            </small>
                          </div>
                          {verificacion.actividadPaso?.actividad?.actividadtipo
                            ?.nombre && (
                            <small
                              css={{
                                color: "var(--grisTextos)",
                                display: "block",
                              }}
                            >
                              {
                                verificacion.actividadPaso.actividad
                                  .actividadtipo.nombre
                              }
                            </small>
                          )}
                          {verificacion.comentario && (
                            <small
                              css={{
                                display: "block",
                                marginTop: "0.25em",
                                fontStyle: "italic",
                              }}
                            >
                              "{verificacion.comentario}"
                            </small>
                          )}
                          {verificacion.archivoId && (
                            <div css={{ marginTop: "0.5em" }}>
                              {imagenesVerificaciones[verificacion.id] ? (
                                <Image
                                  src={imagenesVerificaciones[verificacion.id]}
                                  alt="Imagen de verificación"
                                  width="150"
                                  preview
                                  imageStyle={{ borderRadius: "4px" }}
                                />
                              ) : (
                                <div
                                  css={{
                                    width: "150px",
                                    height: "100px",
                                    backgroundColor: "#e0e0e0",
                                    borderRadius: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <i className="pi pi-spin pi-spinner"></i>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Determinar estado del certificado
  const getCertificadoEstado = () => {
    const certificado = procesoAuditado?.proceso?.certificado?.[0];
    if (!certificado) return { pendiente: true };

    const estado = certificado.estadocertificado;
    if (estado?.vigentebool) {
      return { vigente: true, fechaEmision: certificado.fechaemision };
    } else if (estado?.bajadefinitiva) {
      return { anulado: true };
    } else if (estado?.nombre) {
      return { suspendido: true };
    }
    return { pendiente: true };
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />
      {loader && (
        <div>
          {/* Skeleton del título */}
          <Skeleton
            width="20rem"
            height="1.5rem"
            css={{ marginBottom: "0.5rem", marginTop: "2em" }}
          />

          <div className="grid">
            {/* Skeleton del estado del certificado */}
            <div className="col-12 lg:col-6">
              <Card>
                <div
                  css={{ display: "flex", alignItems: "center", gap: "1rem" }}
                >
                  <Skeleton shape="circle" size="3rem" />
                  <div>
                    <Skeleton
                      width="8rem"
                      height="1.2rem"
                      css={{ marginBottom: "0.5rem" }}
                    />
                    <Skeleton width="6rem" height="1rem" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Skeleton de datos del proceso */}
            <div className="col-12">
              <Card>
                <Skeleton
                  width="12rem"
                  height="1.5rem"
                  css={{ marginBottom: "1rem" }}
                />
                <div className="grid">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                    <div className="col-12 md:col-6" key={i}>
                      <Skeleton
                        width="6rem"
                        height="0.9rem"
                        css={{ marginBottom: "0.3rem" }}
                      />
                      <Skeleton width="10rem" height="1.1rem" />
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            {/* Skeleton de productos */}
            <div className="col-12">
              <Card>
                <Skeleton
                  width="8rem"
                  height="1.5rem"
                  css={{ marginBottom: "1rem" }}
                />
                <div
                  css={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                  }}
                >
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} width="100%" height="2.5rem" />
                  ))}
                </div>
              </Card>
            </div>

            {/* Skeleton del flujo del proceso */}
            <div className="col-12 lg:col-6">
              <Card>
                <Skeleton
                  width="10rem"
                  height="1.5rem"
                  css={{ marginBottom: "1rem" }}
                />
                <div
                  css={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                  }}
                >
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      css={{
                        display: "flex",
                        gap: "1rem",
                        alignItems: "center",
                      }}
                    >
                      <Skeleton shape="circle" size="2rem" />
                      <div css={{ flex: 1 }}>
                        <Skeleton
                          width="70%"
                          height="1rem"
                          css={{ marginBottom: "0.3rem" }}
                        />
                        <Skeleton width="50%" height="0.8rem" />
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            {/* Skeleton de descargas */}
            <div className="col-12 lg:col-6">
              <Card>
                <Skeleton
                  width="8rem"
                  height="1.5rem"
                  css={{ marginBottom: "1rem" }}
                />
                <Skeleton width="12rem" height="2.5rem" />
              </Card>
            </div>
          </div>
        </div>
      )}
      {!loader && procesoAuditado && (
        <div>
          <Titulo urlVolver={`/auditoriasg/${auditoriaId}`}>
            {procesoAuditado.proceso.nombre}
            {" - "}
            {procesoAuditado.proceso.nombrefantasia}
          </Titulo>

          <div className="grid">
            <div className="col-12 lg:col-6">
              <EstadoCertificado
                {...getCertificadoEstado()}
                sinRenovacion={
                  procesoAuditado?.proceso?.certificadotipo?.sinrenovacion
                }
              />
            </div>

            <div className="col-12">
              <Card>
                <Titulo>Datos del proceso</Titulo>
                <div className="grid">
                  <CampoInformacion nombreCampo="Cliente">
                    {procesoAuditado.proceso.cliente?.razonsocial}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Tipo de certificado">
                    {procesoAuditado.proceso.certificadotipo?.nombre}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Reglamentación">
                    {procesoAuditado.proceso.certificadotipo?.reglamentacion}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Norma">
                    {procesoAuditado.proceso.norma?.numero}
                    {procesoAuditado.proceso.norma?.ano &&
                      `:${procesoAuditado.proceso.norma.ano}`}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Fabricante">
                    {procesoAuditado.proceso.sitioFabricante?.nombre || "-"}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Dirección del fabricante">
                    {procesoAuditado.proceso.sitioFabricante?.direccion || "-"}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Fábrica">
                    {procesoAuditado.proceso.sitioFabrica?.nombre || "-"}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Dirección de la fábrica">
                    {procesoAuditado.proceso.sitioFabrica?.direccion || "-"}
                  </CampoInformacion>
                </div>
              </Card>
            </div>

            {procesoAuditado.proceso.producto?.length > 0 && (
              <div className="col-12">
                <Card>
                  <Titulo>Productos</Titulo>
                  <TablaFiltrable conFiltros={false}
                    value={procesoAuditado.proceso.productosParaTabla}
                    emptyMessage="No hay productos"
                    size="small"
                    stripedRows
                    expandedRows={expandedRows}
                    onRowToggle={(e) => setExpandedRows(e.data)}
                    rowExpansionTemplate={rowExpansionTemplate}
                  >
                    <Column expander style={{ width: "3em" }} />
                    {Object.entries(
                      procesoAuditado.proceso.camposParaTabla || {},
                    ).map(([campoId, etiqueta]) => (
                      <Column key={campoId} field={campoId} header={etiqueta} />
                    ))}
                    <Column
                      body={actionBodyTemplate}
                      style={{ width: "3em", textAlign: "center" }}
                    />
                  </TablaFiltrable>
                </Card>
              </div>
            )}

            {(procesoAuditado.proceso.actividad?.length > 0 ||
              procesoAuditado.proceso.solicitudproceso) && (
              <div className="col-12 lg:col-6">
                <Card>
                  <Titulo>Flujo del proceso</Titulo>
                  <ActividadesTimeline
                    actividades={procesoAuditado.proceso.actividad}
                    solicitud={procesoAuditado.proceso.solicitudproceso}
                    revisorSolicitud={procesoAuditado.proceso.usuariocreador}
                    cambiosEstado={procesoAuditado.proceso.certificado?.[0]?.cambioestadocertificado}
                    formacionPorAuditor={procesoAuditado.formacionPorAuditor}
                    onDescargarEvidencia={(archivoHash, nombreArchivo, onComplete) => {
                      downloadFile(
                        `archivo/${archivoHash}/download`,
                        nombreArchivo,
                        onComplete,
                        (error) => {
                          onComplete();
                          toast.current?.show({
                            severity: "error",
                            summary: "Error",
                            detail: error[0]?.msg || "No se pudo descargar el archivo",
                            sticky: true,
                          });
                        },
                      );
                    }}
                  />
                </Card>
              </div>
            )}

            <div className="col-12 lg:col-6">
              {tieneCertificadoPDF() && (
                <Card css={{ marginBottom: "1em" }}>
                  <Titulo>Descargas</Titulo>
                  <Boton
                    callback={descargarCertificado}
                    loader={loaderDescarga}
                  >
                    <i
                      className="pi pi-download"
                      css={{ marginRight: "0.5em" }}
                    ></i>
                    Descargar certificado
                  </Boton>
                </Card>
              )}

              {procesoAuditado.proceso.solicitudproceso?.PropuestaComercial
                ?.length > 0 && (
                <Card css={{ marginBottom: "1em" }}>
                  <Titulo>Propuestas comerciales</Titulo>
                  <div
                    css={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.5em",
                    }}
                  >
                    {procesoAuditado.proceso.solicitudproceso.PropuestaComercial.map(
                      (propuesta) => (
                        <div
                          key={propuesta.id}
                          css={{
                            padding: "0.75em",
                            backgroundColor: "#f8f9fa",
                            borderRadius: "6px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div>
                            <div css={{ fontWeight: "bold" }}>
                              Propuesta comercial{" "}
                              {formatFecha(propuesta.fechaemision)}
                            </div>
                            {propuesta.fechaaceptacion && (
                              <small css={{ color: "var(--verde)" }}>
                                Aceptada el{" "}
                                {formatFecha(propuesta.fechaaceptacion)}
                              </small>
                            )}
                            {!propuesta.fechaaceptacion && (
                              <small css={{ color: "var(--amarillo)" }}>
                                Pendiente de aceptación
                              </small>
                            )}
                          </div>
                          <Boton
                            uri={`/propuesta/${propuesta.id}?from=auditoriasg&auditoriaId=${auditoriaId}&procesoId=${procesoAuditadoId}`}
                            icono
                            chico
                            negro
                          >
                            <i className="pi pi-arrow-right"></i>
                          </Boton>
                        </div>
                      ),
                    )}
                  </div>
                </Card>
              )}

              {getArchivos().length > 0 && (
                <Card>
                  <Titulo>Archivos</Titulo>
                  <div
                    css={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.5em",
                    }}
                  >
                    {getArchivos().map((archivo) => (
                      <div
                        key={`${archivo.origen}-${archivo.id}`}
                        css={{
                          padding: "0.75em",
                          backgroundColor: "#f8f9fa",
                          borderRadius: "6px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: "1em",
                        }}
                      >
                        <div css={{ flex: 1, minWidth: 0 }}>
                          <div
                            css={{
                              fontWeight: "bold",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <i
                              className="pi pi-file"
                              css={{ marginRight: "0.5em" }}
                            ></i>
                            <ArchivoPreview
                              nombre={archivo.nombre}
                              hash={archivo.hash}
                            />
                          </div>
                          <small css={{ color: "var(--grisTextos)" }}>
                            {archivo.origen}
                            {archivo.fecha &&
                              ` - ${formatFecha(archivo.fecha)}`}
                          </small>
                        </div>
                        <Boton
                          callback={() => descargarArchivoActividad(archivo)}
                          icono
                          chico
                          negro
                          loader={archivoDescargando === archivo.id}
                        >
                          <i className="pi pi-download"></i>
                        </Boton>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
